import i18n from "@/lib/i18n/i18n"
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { Participant } from "@/reducers/chatsSlice"
import { v4 as uuid } from "uuid"
import {
  UploadedFile,
  useExtractDataFromOrder,
} from "@/hooks/useExtractDataFromOrder"
import { useLocalStorage } from "usehooks-ts"
import { OrderAnalyzeRoute } from "@/lib/orderAnalyzeRouteParser"
import {
  OrderMetaData,
  StopMetaData,
} from "@/components/maps/AnalyzeOrderVerifyStops"
import { findRoute2g, RouteStop } from "@/reducers/routes2gSlice"
import { stripInvalidXMLChars } from "@/lib/utils"
import FileUploadApi from "@/api/fileUploadApi"
import { CarRouteConfig } from "@/api/adHocCommands"

export type OrderSource = "file" | "text"
import XmppPubSubApi from "@/api/xmppPubSubApi"
import { useEventCallback } from "@/hooks/use-event-callback"
import useActiveChatDriver from "@/hooks/useActiveChatDriver"
import { useAppSelector, useAppStore } from "@/reducers/hooks"
import { selectActiveChat } from "@/reducers/chatsSliceSelectors"
import { selectRouteJidFromChatJid } from "@/reducers/selectors"
import { useFeatureFlagEnabled } from "posthog-js/react"
import {
  stopIds,
  withoutStopsGoneFromRoute,
} from "@/lib/analyzeOrderRouteStops"
import { XmppContext } from "@/stream/xmppClient"
import { useNaviConfig } from "@/hooks/useNaviConfig"

interface AnalyzeOrderContextType {
  driver: Participant | undefined
  driverJid: string | undefined
  routeJid: string | undefined
  carConfig: CarRouteConfig | undefined
  messageId: string
  setMessageId: (messageId: string) => void
  isMapOpen: boolean
  isVerifyOpen: boolean
  setMapOpen: (value: boolean) => void
  setVerifyOpen: (value: boolean) => void
  isModalHidden: boolean
  setModalHidden: (value: boolean) => void
  files: UploadedFile[] | null
  setFiles: (files: UploadedFile[] | null) => void
  isRetrying: boolean
  setIsRetrying: (value: boolean) => void
  wasEdited: boolean
  setWasEdited: (value: boolean) => void
  shouldSkipVerifyStep: boolean
  isRouteFetched: boolean
  setIsRouteFetched: (value: boolean) => void
  messageGenerated: boolean
  setMessageGenerated: (value: boolean) => void
  messageText: string
  setMessageText: (value: string) => void
  handleResetMessageGenerated: () => void
  isProcessing: boolean
  startProcessing: (uploadedFiles: UploadedFile[]) => Promise<void>
  isRouteDetected: boolean
  routeData: OrderAnalyzeRoute | undefined
  setRouteData: (data: OrderAnalyzeRoute) => void
  routeMetaData: OrderMetaData | undefined
  stops: RouteStop[] | undefined
  // Ids of the stops on the driver's route when this analysis started — what
  // the plan was built on. Anything else in the plan was added by this flow.
  baseStopIds: ReadonlySet<string>
  resetExtractingState: () => void
  isFailed: boolean
  fetchPolylines: (
    stops: RouteStop[],
    routeMetaData: OrderMetaData,
  ) => Promise<void>
  isFetchingPolylines: boolean
  isFetchingPolylinesFailed: boolean
  polylineErrors: string[] | null
  clearPolylineErrors: () => void
  originalRouteMetaData: OrderMetaData | null
  setOriginalRouteMetaData: (data: OrderMetaData | null) => void
  handleCloseMap: (wasSent?: boolean) => void
  retryProcessing: () => void
  handleBackToVerify: (routeData: OrderAnalyzeRoute) => void
  handleOnEdit: () => void
  handleContinue: (
    metaData: OrderMetaData,
    shouldFetch?: boolean,
  ) => Promise<void>
  handleFileUpload: (
    files: FileList,
    fileInput: HTMLInputElement | null,
    source?: OrderSource,
    textContent?: string,
  ) => Promise<void>
  handleCreateStandstillOrder: () => void
  isLoading: boolean
  hideCoordinates: boolean
  hideStopTimes: boolean
  showFixedTimes: boolean
  naviTransportId?: number
  setNaviTransportId: (id: number | undefined) => void
  naviSelection?: import("@/api/adHocCommands").NaviSelectionInput
  setNaviSelection: (
    s: import("@/api/adHocCommands").NaviSelectionInput | undefined,
  ) => void
}

// Pull the structured address fields out of the Google place. They live in
// addressComponents (the same source we already used for postalCode); the AI
// extraction only ever fills formattedAddress/companyName/postalCode, so when
// a real Google place is present we prefer its richer components. Street is
// assembled from route + street_number; a city-only pick has neither.
const addressFromPlace = (place: google.maps.places.Place | undefined) => {
  const components = place?.addressComponents
  if (!components) return undefined
  const find = (type: string) => components.find((c) => c.types.includes(type))
  const route = find("route")?.longText || undefined
  const streetNumber = find("street_number")?.longText || undefined
  return {
    street: route
      ? streetNumber
        ? `${route} ${streetNumber}`
        : route
      : undefined,
    locality: find("locality")?.longText || undefined,
    postalCode: find("postal_code")?.longText || undefined,
    country: find("country")?.longText || undefined,
    countryCode: find("country")?.shortText || undefined,
  }
}

const createRouteStopsFromMetaData = (metaData: OrderMetaData) =>
  metaData.stops?.map((metaDataStop: StopMetaData) => {
    const scheduledTimeMax = metaDataStop.scheduled_hour_max
      ? metaDataStop.scheduled_hour_max
      : "23:59:00"
    const scheduledDate = metaDataStop.scheduled_arrival_time
      ? metaDataStop.scheduled_arrival_time.split("T")[0]
      : undefined
    const scheduled_arrival_time = scheduledDate
      ? `${scheduledDate}T${scheduledTimeMax}`
      : undefined
    const placeAddress = addressFromPlace(metaDataStop.place)
    return {
      action: metaDataStop.action,
      location: {
        formattedAddress:
          metaDataStop.place?.formattedAddress ??
          ` ${metaDataStop.location?.formattedAddress}`,
        companyName: metaDataStop.location.companyName,
        // Prefer the Google place picked by the dispatcher; fall back to the
        // extraction's structured fields (incl. document GPS coordinates) so
        // they survive the context-level rebuild on route regeneration.
        street: placeAddress?.street ?? metaDataStop.location?.street,
        locality: placeAddress?.locality ?? metaDataStop.location?.locality,
        country: placeAddress?.country ?? metaDataStop.location?.country,
        countryCode:
          placeAddress?.countryCode ?? metaDataStop.location?.countryCode,
        postalCode:
          placeAddress?.postalCode ?? metaDataStop.location?.postalCode,
        lat: metaDataStop.location?.lat,
        lon: metaDataStop.location?.lon,
      },
      addressUpdated: true,
      action_reference_number: metaDataStop.action_reference_number,
      ref: metaData.order_number,
      scheduled_hour_min: metaDataStop.scheduled_hour_min,
      scheduled_hour_max: metaDataStop.scheduled_hour_max,
      time_fixed: metaDataStop.time_fixed,
      pallet_count: metaDataStop.pallet_count,
      weight_kg: metaDataStop.weight_kg,
      scheduled_arrival_time,
    } as RouteStop
  })

const AnalyzeOrderContext = createContext<AnalyzeOrderContextType | undefined>(
  undefined,
)

export function AnalyzeOrderProvider({ children }: { children: ReactNode }) {
  // Current driver from active chat. In context of this driver we are analyzing the order
  const driver = useActiveChatDriver()
  const activeChat = useAppSelector(selectActiveChat)
  const routeJid = useAppSelector(selectRouteJidFromChatJid(activeChat.jid))

  // Which step i currently open
  const [isMapOpen, setMapOpen] = useState(false)
  const [isVerifyOpen, setVerifyOpen] = useState(false)

  // When sending message and setting route we just hide the modal instead of closing it
  // so if there is an error we can show it again without losing state
  const [isModalHidden, setModalHidden] = useState(false)

  // We use messageId to track analytics events for this analyze order session
  const [messageId, setMessageId] = useState<string>(uuid())

  // Files selected to be analyzed
  const [files, setFiles] = useState<UploadedFile[] | null>(null)

  // Are we retrying processing with the same file(s)
  const [isRetrying, setIsRetrying] = useState(false)

  // Flag to indicate if user edited any of stops extracted from documents
  const [wasEdited, setWasEdited] = useState(false)

  // User preference to skip verify step and go straight to map
  const [skipVerifyStepPreference] = useLocalStorage(
    "shouldSkipVerifyStep",
    false,
  )

  // With Marcos (Navigator) configured the order is created in the verify
  // step, so that step is never skipped, whatever the dispatcher chose with
  // "skip this step next time". An oskargroup dispatcher who had it on got
  // only the driver message and created nothing in Marcos (2026-11-03).
  // Until the config is known the step is not skipped either.
  const { myJid } = useContext(XmppContext)
  const naviConfig = useNaviConfig(myJid?.domain)
  const shouldSkipVerifyStep =
    skipVerifyStepPreference && !naviConfig.configured && !naviConfig.loading

  // Flag to indicate if route polylines have been fetched
  const [isRouteFetched, setIsRouteFetched] = useState(false)

  // Flag to indicate if message has been generated
  const [messageGenerated, setMessageGenerated] = useState(false)

  // Message text generated
  const [messageText, setMessageText] = useState("")

  const hideCoordinates = Boolean(
    useFeatureFlagEnabled("hide_coordinates_in_route_messages"),
  )

  const hideStopTimes = Boolean(
    useFeatureFlagEnabled("hide_stop_times_in_route_messages"),
  )

  const showFixedTimes = Boolean(useFeatureFlagEnabled("fixed-stop-times"))

  // Navi integration state
  const [naviTransportId, setNaviTransportId] = useState<number | undefined>()
  const [naviSelection, setNaviSelection] = useState<
    import("@/api/adHocCommands").NaviSelectionInput | undefined
  >()

  // Store original route metadata for analytics comparison
  const [originalRouteMetaData, setOriginalRouteMetaData] =
    useState<OrderMetaData | null>(null)

  const handleResetMessageGenerated = useCallback(() => {
    setMessageGenerated(false)
    setMessageText("")
  }, [])

  const setSanitizedMessageText = useCallback(
    (text: string) => setMessageText(stripInvalidXMLChars(text)),
    [],
  )

  const {
    isProcessing,
    startProcessing,
    isRouteDetected,
    routeData,
    setRouteData,
    routeMetaData,
    carConfig,
    setCarConfig,
    stops,
    resetExtractingState,
    setManualOrder,
    isFailed,
    fetchPolylines,
    isFetchingPolylines,
    isFetchingPolylinesFailed,
    polylineErrors,
    clearPolylineErrors,
  } = useExtractDataFromOrder({
    fetchOnlyMetaData: !shouldSkipVerifyStep,
    messageId,
  })

  // The driver's route as this analysis found it. Read from the store once,
  // when the flow starts, rather than tracked: the live route keeps moving
  // while the dispatcher works (the driver leaves a stop, a TMS sync adds one),
  // and the save has to tell those changes apart from the order's own stops.
  const store = useAppStore()
  const liveRouteStops = () => findRoute2g(routeJid)(store.getState())?.stops
  const [routeStopIdsAtStart, setRouteStopIdsAtStart] = useState<
    ReadonlySet<string>
  >(new Set())
  const snapshotRouteAtStart = () =>
    setRouteStopIdsAtStart(stopIds(liveRouteStops() ?? []))

  // The extraction hands back the route's existing stops with their ids and
  // the order's stops without; both views of "already on the route" count.
  const baseStopIds = useMemo(
    () => new Set([...routeStopIdsAtStart, ...stopIds(stops ?? [])]),
    [routeStopIdsAtStart, stops],
  )

  const resetAll = useCallback(() => {
    resetExtractingState()
    setIsRetrying(false)
    setIsRouteFetched(false)
    setMapOpen(false)
    setVerifyOpen(false)
    setModalHidden(false)
    setFiles([])
    setWasEdited(false)
    setOriginalRouteMetaData(null)
    setMessageGenerated(false)
    setMessageText("")
    setNaviTransportId(undefined)
    setNaviSelection(undefined)
  }, [resetExtractingState])

  // Handle closing the map and resetting all state
  const handleCloseMap = useEventCallback((wasSent?: boolean) => {
    if (!wasSent) {
      window.analytics.track("AnalyzeOrderFailed", {
        reason: "user_closed_modal",
        wasProcessing: isProcessing,
        wasRouteDetected: isRouteDetected,
        wasFailed: isFailed,
        filesNames: files?.map((uploadedFile) => uploadedFile.name),
        messageId: messageId,
      })
    }
    resetAll()
  })

  // Retry processing with current file
  const retryProcessing = useEventCallback(() => {
    if (!files || files.length === 0) return

    // Reset processing state and restart
    resetExtractingState()
    setIsRetrying(true)
    setVerifyOpen(true)
    setMessageId(uuid())
    snapshotRouteAtStart()
    try {
      startProcessing(files)
    } catch (error) {
      console.error("Retry failed:", error)
      window.analytics.track("AnalyzeOrderFailed", {
        reason: "retry_failed",
        error: error instanceof Error ? error.message : "Unknown error",
        files: files.map((uploadedFile) => uploadedFile.name),
        messageId: messageId,
      })
      resetExtractingState()
      setIsRetrying(false)
      setMapOpen(false)
      setVerifyOpen(false)
    } finally {
      // Reset retry state after a short delay to allow the UI to update
      setTimeout(() => setIsRetrying(false), 100)
    }
  })

  const handleBackToVerify = useEventCallback(
    (routeData: OrderAnalyzeRoute) => {
      setRouteData(routeData)
      setMapOpen(false)
      setVerifyOpen(true)
      setCarConfig({
        ...carConfig,
        avoid: routeData.avoidOptions,
        loadType: routeData.loadType,
      })
    },
  )

  const handleOnEdit = useEventCallback(() => {
    setIsRouteFetched(false)
    setWasEdited(true)
    setMessageGenerated(false)
    setMessageText("")
  })

  const handleContinue = useEventCallback(
    async (metaData: OrderMetaData, shouldFetch?: boolean) => {
      // Proceed to fetch polylines and show map
      if (stops) {
        // Separate existing stops with IDs from new stops. A stop that has
        // left the driver's route since the analysis started stays out of the
        // plan: gps would give it a fresh id and it would be saved as part of
        // this order.
        const existingStops = withoutStopsGoneFromRoute(
          stops.filter((stop: RouteStop) => stop.id),
          baseStopIds,
          liveRouteStops(),
        )

        // Build new stops from metadata
        const newStops = createRouteStopsFromMetaData(metaData)

        try {
          // If route is not fetched yet or user edited stops, fetch polylines
          if (!isRouteFetched || shouldFetch) {
            // Fetch car config for the driver
            const initialCarConfig =
              (await XmppPubSubApi.fetchCarConfig(
                window.XMPP!,
                routeJid || "",
              )) ?? {}
            setCarConfig({
              ...initialCarConfig,
              avoid: initialCarConfig.avoid ?? [],
              loadType: initialCarConfig.loadType ?? [],
            })

            // Fetch polylines for all stops with current car config

            await fetchPolylines(
              [...existingStops, ...(newStops ?? [])],
              metaData,
              initialCarConfig,
            )
            setIsRouteFetched(true)
          }

          // Go to map view
          setVerifyOpen(false)
          setMapOpen(true)
        } catch (error) {
          console.error(error)
        }
      }
    },
  )

  // Handle file upload and send
  const handleFileUpload = useEventCallback(
    async (
      files: FileList,
      fileInput: HTMLInputElement | null,
      source: OrderSource = "file",
      textContent?: string,
    ) => {
      if (!routeJid || files.length === 0) return
      let uploadedFiles: UploadedFile[]
      try {
        // Upload all files and get their URLs

        const uploadPromises = Array.from(files).map(async (file) => {
          const urls = await FileUploadApi.requestSlot(
            window.XMPP!,
            file.name,
            file.size.toString(),
            file.type,
          )
          const [get, put] = urls

          await fetch(put, {
            method: "PUT",
            body: file,
          })
          return { url: get, file, name: file.name }
        })

        uploadedFiles = await Promise.all(uploadPromises)

        setFiles(uploadedFiles)
        // Reset any previous state and start processing
        resetExtractingState()
        const newMessageId = uuid()
        setMessageId(newMessageId)
        snapshotRouteAtStart()
        startProcessing(uploadedFiles)
        window.analytics.track("AnalyzeOrderStart", {
          uploadedFiles,
          messageId: newMessageId,
          source,
          ...(source === "text" && textContent ? { textContent } : {}),
        })

        // Navigate to appropriate step based on user preference

        if (shouldSkipVerifyStep) {
          setIsRouteFetched(true)
          setMapOpen(true)
          setVerifyOpen(false)
        } else {
          setMapOpen(false)
          setVerifyOpen(true)
        }
      } catch (error) {
        console.error("Upload failed:", error)
        window.analytics.track("FileUploadError", { error })
        window.analytics.track("AnalyzeOrderFailed", {
          reason: "file_upload_error",
          error: error instanceof Error ? error.message : "Unknown error",
          messageId: messageId,
        })
        window.appToaster.show({
          message: i18n.t("app.analyzeOrder.uploadFailed"),
          intent: "danger",
          timeout: 5000,
          action: {
            text: i18n.t("app.analyzeOrder.selectAgain"),
            onClick: () => fileInput?.click(),
          },
        })
        // Reset state on error
        resetExtractingState()
        setMapOpen(false)
        setVerifyOpen(false)
      }
    },
  )

  // Zlecenie postojowe (standstill/demurrage order — driver waiting, vehicle
  // service, etc.): no source document, so skip upload + LLM extraction and
  // open verify directly with the Marcos-side defaults from the "ZLECENIE
  // POSTOJOWE" contractor (matched by name in NaviOrderPanel, same as any
  // other contractor). The dispatcher fills in the stops and can change
  // anything else before submitting, same as a regular order.
  const handleCreateStandstillOrder = useEventCallback(() => {
    if (!routeJid) return
    resetExtractingState()
    setFiles(null)
    const newMessageId = uuid()
    setMessageId(newMessageId)
    snapshotRouteAtStart()
    // Pickup + delivery, both today at 12:00 — the dispatcher fills in the
    // actual address/time, same as any manually added stop.
    const today = new Date().toISOString().split("T")[0]
    const defaultStop = {
      location: {
        formattedAddress: "Słubicka 43, 69-100 Kunowice",
        companyName: "Euromaster Wlazak",
        street: "Słubicka 43",
        locality: "Kunowice",
        postalCode: "69-100",
        country: "Poland",
        countryCode: "PL",
      },
      scheduled_arrival_time: `${today}T12:00:00`,
      scheduled_hour_min: "12:00:00",
      scheduled_hour_max: "12:00:00",
      time_fixed: false,
    }
    setManualOrder(
      {
        order_number: "serwis",
        ref_number: "",
        notes: "",
        weight: "",
        price_netto_eur: 1,
        customer_name: "ZLECENIE POSTOJOWE",
        recipient_name: "ZLECENIE POSTOJOWE",
        stops: [],
      },
      [
        { ...defaultStop, action: "pickup" },
        { ...defaultStop, action: "delivery" },
      ],
    )
    window.analytics.track("AnalyzeOrderStart", {
      messageId: newMessageId,
      source: "standstill",
    })
    setMapOpen(false)
    setVerifyOpen(true)
  })

  // Store original route metadata when it becomes available
  useEffect(() => {
    if (routeMetaData && !originalRouteMetaData) {
      setOriginalRouteMetaData(routeMetaData)
    }
  }, [routeMetaData, originalRouteMetaData, setOriginalRouteMetaData])

  const contextValue: AnalyzeOrderContextType = {
    driver,
    driverJid: driver?.jid,
    routeJid,
    carConfig,
    isMapOpen,
    setVerifyOpen,
    isVerifyOpen,
    setMapOpen,
    isModalHidden,
    setModalHidden,
    messageId,
    setMessageId,
    files,
    setFiles,
    isRetrying,
    setIsRetrying,
    wasEdited,
    setWasEdited,
    shouldSkipVerifyStep,
    isRouteFetched,
    setIsRouteFetched,
    messageGenerated,
    setMessageGenerated,
    messageText,
    setMessageText: setSanitizedMessageText,
    handleResetMessageGenerated,
    isProcessing,
    startProcessing,
    isRouteDetected,
    routeData,
    setRouteData,
    routeMetaData,
    stops,
    baseStopIds,
    resetExtractingState,
    isFailed,
    fetchPolylines,
    isFetchingPolylines,
    isFetchingPolylinesFailed,
    polylineErrors,
    clearPolylineErrors,
    setOriginalRouteMetaData,
    originalRouteMetaData,
    handleCloseMap,
    retryProcessing,
    handleBackToVerify,
    handleOnEdit,
    handleContinue,
    handleFileUpload,
    handleCreateStandstillOrder,
    isLoading: isProcessing && !isRouteDetected,
    hideCoordinates,
    hideStopTimes,
    showFixedTimes,
    naviTransportId,
    setNaviTransportId,
    naviSelection,
    setNaviSelection,
  }

  return (
    <AnalyzeOrderContext.Provider value={contextValue}>
      {children}
    </AnalyzeOrderContext.Provider>
  )
}

export function useAnalyzeOrderContext() {
  const context = useContext(AnalyzeOrderContext)
  if (context === undefined) {
    throw new Error(
      "useAnalyzeOrder must be used within a AnalyzeOrderProvider",
    )
  }
  return context
}

// Like useAnalyzeOrderContext but returns undefined instead of throwing when
// no provider is mounted. For components shared across trees where the
// provider is optional (e.g. AnalyzeOrderVerify in the Plan Order flow).
export function useAnalyzeOrderContextOptional() {
  return useContext(AnalyzeOrderContext)
}
